// src/hooks/useWallet.js

import { useState, useEffect } from 'react';
import { 
  connectWallet, 
  addWorldLandNetwork, 
  isWorldLandNetwork,
  getNetworkInfo,
  getWLCBalance,
  isMetaMaskInstalled,
  getMetaMaskProvider
} from '../utils/wallet';

export const useWallet = () => {
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState('0');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [networkInfo, setNetworkInfo] = useState(null);
  const [error, setError] = useState(null);

  // 계정 변경 감지
  useEffect(() => {
    const provider = getMetaMaskProvider();
    if (!provider) return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        setAccount(null);
        setBalance('0');
      } else {
        setAccount(accounts[0]);
        updateBalance(accounts[0]);
      }
    };

    const handleChainChanged = () => {
      // 네트워크 변경시 페이지 새로고침 (권장사항)
      window.location.reload();
    };

    provider.on('accountsChanged', handleAccountsChanged);
    provider.on('chainChanged', handleChainChanged);

    // 초기 연결 상태 확인
    checkInitialConnection();
    checkNetwork();

    return () => {
      if (provider) {
        provider.removeListener('accountsChanged', handleAccountsChanged);
        provider.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []);

  // 초기 연결 상태 확인
  const checkInitialConnection = async () => {
    const provider = getMetaMaskProvider();
    if (!provider) return;

    try {
      const accounts = await provider.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        setAccount(accounts[0]);
        updateBalance(accounts[0]);
      }
    } catch (error) {
      console.error('초기 연결 확인 실패:', error);
    }
  };

  // 네트워크 확인
  const checkNetwork = async () => {
    try {
      const isCorrect = await isWorldLandNetwork();
      setIsCorrectNetwork(isCorrect);
      
      const netInfo = await getNetworkInfo();
      setNetworkInfo(netInfo);
    } catch (error) {
      console.error('네트워크 확인 실패:', error);
      setIsCorrectNetwork(false);
      setNetworkInfo(null);
    }
  };

  // 잔액 업데이트
  const updateBalance = async (address) => {
    try {
      const wlcBalance = await getWLCBalance(address);
      setBalance(wlcBalance);
    } catch (error) {
      console.error('잔액 업데이트 실패:', error);
    }
  };

  // 지갑 연결
  const connect = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const address = await connectWallet();
      setAccount(address);
      await updateBalance(address);
      await checkNetwork();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsConnecting(false);
    }
  };

  // 네트워크 추가/전환
  const switchToWorldLand = async (networkType = 'testnet') => {
    setError(null);

    try {
      await addWorldLandNetwork(networkType);
      // 네트워크 변경 후 체크는 chainChanged 이벤트에서 자동으로 처리됨
    } catch (error) {
      setError(error.message);
    }
  };

  // 연결 해제
  const disconnect = () => {
    setAccount(null);
    setBalance('0');
    setIsCorrectNetwork(false);
    setNetworkInfo(null);
    setError(null);
  };

  return {
    account,
    balance,
    isConnecting,
    isCorrectNetwork,
    networkInfo,
    error,
    isMetaMaskInstalled: isMetaMaskInstalled(),
    connect,
    disconnect,
    switchToWorldLand,
    updateBalance: () => account && updateBalance(account),
    checkNetwork
  };
};